import { supabase } from '../config/supabase';
import { ClientReportSettingsService } from '../features/assessments-v2/services/ClientReportSettingsService';
import type { ClientReportSettings } from '../features/assessments-v2/types';
import type { Client } from './UserService';
import { getClientBrandingPublicUrl } from '../utils/storage';
import { digitsOnly } from '../utils/contact';
import { DEFAULT_TIME_ZONE, isValidTimeZone } from '../utils/timeZone';

const pickFirst = (...values: Array<string | null | undefined>) =>
  values.find((value) => typeof value === 'string' && value.trim().length > 0)?.trim() ?? '';

const normalizeText = (value?: string | null) => value?.trim() ?? '';

const resolveStoredTimeZone = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return DEFAULT_TIME_ZONE;
  if (!isValidTimeZone(value)) {
    throw new Error('A clínica possui um fuso horário inválido no banco.');
  }
  return value.trim();
};

const buildAddressLine1 = (input: {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
}) => {
  const base = [input.street, input.number].filter(Boolean).join(', ');
  const suffix = [input.complement, input.district].filter(Boolean).join(' - ');
  return [base, suffix].filter(Boolean).join(', ').trim();
};

const buildAddressLine2 = (input: {
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}) =>
  [
    input.city && input.state ? `${input.city} - ${input.state}` : input.city || input.state,
    input.postalCode ? `CEP: ${input.postalCode}` : '',
  ]
    .filter(Boolean)
    .join(' | ')
    .trim();

const resolveStoredLogoUrl = (path?: string | null, legacyUrl?: string | null) => {
  if (path) {
    return getClientBrandingPublicUrl(path);
  }

  return legacyUrl ?? '';
};

const derivePhoneFull = (value?: string | null) => {
  const digits = digitsOnly(value);
  return digits ? `+${digits}` : '';
};

const deriveWhatsappFull = (value?: string | null) => digitsOnly(value);

const hasAnyConfiguredSetting = (settings: Partial<ClientReportSettings> | null) => {
  if (!settings) return false;

  return Object.values(settings).some((value) => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return Boolean(value);
  });
};

export interface TenantSiteProfile {
  clientId: string;
  clientName: string;
  clientSlug: string | null;
  source: 'db' | 'fallback';
  displayName: string;
  reportSubtitle: string;
  legalName: string;
  cnpj: string;
  logoPrintUrl: string;
  logoMarkUrl: string;
  logoWordmarkUrl: string;
  logoAlt: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement: string;
  addressDistrict: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressLine1: string;
  addressLine2: string;
  fullAddress: string;
  timeZone: string;
  contactPhone: string;
  contactPhoneFull: string;
  contactWhatsapp: string;
  contactWhatsappFull: string;
  contactEmail: string;
  footerLegalLine: string;
}

interface PublicClientProfileRow {
  clientId: string;
  clientName: string;
  clientSlug: string;
  reportDisplayName?: string | null;
  legalName?: string | null;
  cnpj?: string | null;
  reportSubtitle?: string | null;
  logoPrintPath?: string | null;
  logoPrintUrl?: string | null;
  logoMarkPath?: string | null;
  logoMarkUrl?: string | null;
  logoWordmarkPath?: string | null;
  logoWordmarkUrl?: string | null;
  logoAlt?: string | null;
  addressStreet?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  addressDistrict?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressPostalCode?: string | null;
  contactPhone?: string | null;
  contactPhoneFull?: string | null;
  contactWhatsapp?: string | null;
  contactWhatsappFull?: string | null;
  contactEmail?: string | null;
  footerLegalText?: string | null;
}

const toClientReportSettings = (
  row: PublicClientProfileRow,
): ClientReportSettings | null => {
  const settings: Partial<ClientReportSettings> = {
    reportDisplayName: row.reportDisplayName,
    legalName: row.legalName,
    cnpj: row.cnpj,
    reportSubtitle: row.reportSubtitle,
    logoPrintPath: row.logoPrintPath,
    logoPrintUrl: row.logoPrintUrl,
    logoMarkPath: row.logoMarkPath,
    logoMarkUrl: row.logoMarkUrl,
    logoWordmarkPath: row.logoWordmarkPath,
    logoWordmarkUrl: row.logoWordmarkUrl,
    logoAlt: row.logoAlt,
    addressStreet: row.addressStreet,
    addressNumber: row.addressNumber,
    addressComplement: row.addressComplement,
    addressDistrict: row.addressDistrict,
    addressCity: row.addressCity,
    addressState: row.addressState,
    addressPostalCode: row.addressPostalCode,
    contactPhone: row.contactPhone,
    contactPhoneFull: row.contactPhoneFull,
    contactWhatsapp: row.contactWhatsapp,
    contactWhatsappFull: row.contactWhatsappFull,
    contactEmail: row.contactEmail,
    footerLegalText: row.footerLegalText,
  };

  return hasAnyConfiguredSetting(settings) ? (settings as ClientReportSettings) : null;
};

const buildTenantSiteProfile = (input: {
  clientId: string;
  clientName: string;
  clientSlug?: string | null;
  settings: ClientReportSettings | null;
  logoWordmarkFallback?: string | null;
  logoMarkFallback?: string | null;
}): TenantSiteProfile => {
  const {
    clientId,
    clientName,
    clientSlug = null,
    settings,
    logoWordmarkFallback,
    logoMarkFallback,
  } = input;

  const addressStreet = normalizeText(settings?.addressStreet);
  const addressNumber = normalizeText(settings?.addressNumber);
  const addressComplement = normalizeText(settings?.addressComplement);
  const addressDistrict = normalizeText(settings?.addressDistrict);
  const addressCity = normalizeText(settings?.addressCity);
  const addressState = normalizeText(settings?.addressState);
  const addressPostalCode = normalizeText(settings?.addressPostalCode);

  const addressLine1 = buildAddressLine1({
    street: addressStreet,
    number: addressNumber,
    complement: addressComplement,
    district: addressDistrict,
  });
  const addressLine2 = buildAddressLine2({
    city: addressCity,
    state: addressState,
    postalCode: addressPostalCode,
  });

  const contactPhone = pickFirst(settings?.contactPhone, settings?.contactWhatsapp);
  const contactWhatsapp = pickFirst(settings?.contactWhatsapp, settings?.contactPhone);
  const contactPhoneFull = pickFirst(
    settings?.contactPhoneFull,
    derivePhoneFull(settings?.contactPhone),
    derivePhoneFull(settings?.contactWhatsapp),
  );
  const contactWhatsappFull = pickFirst(
    settings?.contactWhatsappFull,
    deriveWhatsappFull(settings?.contactWhatsapp),
    deriveWhatsappFull(settings?.contactPhone),
  );
  const displayName = pickFirst(settings?.reportDisplayName, clientName) || 'Clínica';
  const legalName = normalizeText(settings?.legalName);
  const cnpj = normalizeText(settings?.cnpj);
  const footerLegalLine =
    normalizeText(settings?.footerLegalText) ||
    [legalName, cnpj ? `CNPJ: ${cnpj}` : ''].filter(Boolean).join(' - ') ||
    displayName;

  return {
    clientId,
    clientName,
    clientSlug,
    source: settings ? 'db' : 'fallback',
    displayName,
    reportSubtitle: normalizeText(settings?.reportSubtitle),
    legalName,
    cnpj,
    logoPrintUrl: pickFirst(
      resolveStoredLogoUrl(settings?.logoPrintPath, settings?.logoPrintUrl),
      resolveStoredLogoUrl(settings?.logoWordmarkPath, settings?.logoWordmarkUrl),
      logoWordmarkFallback,
    ),
    logoMarkUrl: pickFirst(
      resolveStoredLogoUrl(settings?.logoMarkPath, settings?.logoMarkUrl),
      logoMarkFallback,
    ),
    logoWordmarkUrl: pickFirst(
      resolveStoredLogoUrl(settings?.logoWordmarkPath, settings?.logoWordmarkUrl),
      resolveStoredLogoUrl(settings?.logoPrintPath, settings?.logoPrintUrl),
      logoWordmarkFallback,
    ),
    logoAlt: pickFirst(settings?.logoAlt, displayName),
    addressStreet,
    addressNumber,
    addressComplement,
    addressDistrict,
    addressCity,
    addressState,
    addressPostalCode,
    addressLine1,
    addressLine2,
    fullAddress: [addressLine1, addressLine2].filter(Boolean).join(', '),
    timeZone: resolveStoredTimeZone(settings?.timeZone),
    contactPhone,
    contactPhoneFull,
    contactWhatsapp,
    contactWhatsappFull,
    contactEmail: normalizeText(settings?.contactEmail),
    footerLegalLine,
  };
};

export class TenantSiteProfileService {
  static async resolveForAuthenticatedClient(selectedClient: Client): Promise<TenantSiteProfile> {
    // Para contexto autenticado, uma falha real de leitura não pode virar
    // silenciosamente o fuso legado: isso permitiria gravar horários no fuso
    // errado. `null` continua sendo ausência legítima de configuração.
    const settings: ClientReportSettings | null = await ClientReportSettingsService.getByClientId(
      selectedClient.clientId,
    );

    return buildTenantSiteProfile({
      clientId: selectedClient.clientId,
      clientName: selectedClient.clientName,
      clientSlug: selectedClient.clientSlug ?? null,
      settings,
      logoWordmarkFallback: pickFirst(
        selectedClient.logo_wordmark_url,
        selectedClient.clientLogoWordmarkUrl,
        selectedClient.logo_url,
        selectedClient.clientLogoUrl,
      ),
      logoMarkFallback: pickFirst(
        selectedClient.logo_url,
        selectedClient.clientLogoUrl,
      ),
    });
  }

  static async resolveForPublicSlug(tenantSlug: string): Promise<TenantSiteProfile | null> {
    const { data, error } = await supabase.rpc('get_public_client_profile_by_slug', {
      p_slug: tenantSlug,
    });

    if (error) {
      console.error('Erro ao carregar o perfil público da clínica:', error);
      throw error;
    }

    const row = (Array.isArray(data) ? data[0] : data) as PublicClientProfileRow | null;
    if (!row?.clientId) {
      return null;
    }

    return buildTenantSiteProfile({
      clientId: row.clientId,
      clientName: row.clientName,
      clientSlug: row.clientSlug,
      settings: toClientReportSettings(row),
    });
  }
}
